import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import qs from "qs";
import axios from "axios";
import jwt from "jwt-decode";

// context to import into components
const AuthContext = React.createContext();

// wrapper around children in App component
const Auth = ({ config, children }) => {
  const history = useHistory();
  const { loaded, applicationClientId, authApiBaseUrl } = config;
  const [user, setUser] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [code, setCode] = useState(null);
  const [token, setToken] = useState(null);
  const sessionTimerDelay = 60000;
  const redirectURL = window.location.origin;

  const clearToken = () => {
    localStorage.removeItem("Hstm.Identity.AccessToken");
  };

  const openAuthWindow = (url, options) => {
    const defaults = {
      title: "hstream",
      width: 600,
      height: 700
    };
    const settings = { ...defaults, ...options };
    const { title, width, height } = settings;
    const y = window.top.outerHeight / 2 + window.top.screenY - height / 2;
    const x = window.top.outerWidth / 2 + window.top.screenX - width / 2;
    return window.open(
      url,
      title,
      `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,copyhistory=no,titlebar=no,width=${width},height=${height},top=${y},left=${x}`
    );
  };

  const signIn = () => {
    const authUrl = `${authApiBaseUrl}/oauth2/authorize?response_type=code&client_id=${applicationClientId}&redirect_uri=${redirectURL}&scope=hstream.profile/userprofile:full+openid`;
    setAuthorizing(true);
    clearToken();

    // IF OLD BROWSER WE CANT USE A SMALL WINDOW, SO WE REDIRECT
    if (
      window.navigator.userAgent.indexOf("Trident/7.0") > -1 ||
      /Edge/.test(window.navigator.userAgent)
    ) {
      window.location.replace(authUrl);
    }

    // FANCY STUFF, OPEN IN A SMALL WINDOW
    else {
      const child = openAuthWindow(authUrl);
      const timer = setInterval(() => {
        if (child.closed) {
          clearInterval(timer);
          setAuthorizing(false);
        }
      }, 200);

      window.addEventListener("focus", () => {
        child.focus();
        window.addEventListener(
          "click",
          () => {
            child.close();
          },
          {
            once: true
          }
        );
      });
    }
  };

  const signOut = () => {
    clearToken();
    window.location.href = `${authApiBaseUrl}/oauth2/logout?client_id=${applicationClientId}&logout_uri=${redirectURL}`;
  };

  const checkSession = () => {
    const decoded = jwt(token);
    if (!decoded || Date.now() >= decoded.exp * 1000) {
      // console.log("Signed out");
      signOut();
    } else {
      // console.log(
      //   `Signing out in ${Math.round(
      //     (decoded.exp * 1000 - Date.now()) / 60000
      //   )} minutes`
      // );
      setTimeout(checkSession, sessionTimerDelay);
    }
  };

  // CHECK FOR SAVED TOKEN
  useEffect(() => {
    setToken(localStorage.getItem("Hstm.Identity.AccessToken"));
  }, []);

  // GET AUTH CODE AND HIDE IT IMMEDIATELY
  useEffect(() => {
    const { location } = history;
    const params = qs.parse(location.search, {
      ignoreQueryPrefix: true
    });
    if (params.code) {
      setCode(params.code);
    }
  }, [history]);

  // USE AUTH CODE TO GET ACCESS TOKEN
  useEffect(() => {
    if (loaded && code) {
      const redirect = `${window.location.origin}`;
      const url = `${authApiBaseUrl}/oauth2/token`;
      axios({
        method: "post",
        url,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        data: qs.stringify({
          grant_type: "authorization_code",
          client_id: applicationClientId,
          code,
          redirect_uri: redirect
        })
      })
        .then(response => {
          setAuthorizing(true);
          setToken(response.data.access_token);
          localStorage.setItem(
            "Hstm.Identity.AccessToken",
            response.data.access_token
          );
        })
        .catch(err => {
          setAuthorizing(false);
          console.log(err);
        });
    }
  }, [loaded, code, applicationClientId, authApiBaseUrl]);

  // USE TOKEN TO GET USER INFO
  useEffect(() => {
    if (loaded && token) {
      if (window.name === "hstream") {
        window.stop();
        window.close();
        window.opener.location.reload();
        window.opener.focus();
      }

      setAuthorized(false);
      setAuthorizing(true);

      axios({
        method: "get",
        url: `${authApiBaseUrl}/userinfo`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
        .then(response => {
          setUser(response.data);
          setAuthorized(true);
          setAuthorizing(false);
          checkSession();
        })
        .catch(err => {
          console.log(err);
          signOut();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, token, authApiBaseUrl]);

  return (
    <AuthContext.Provider
      value={{
        authorizing,
        authorized,
        user,
        signIn,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

Auth.propTypes = {
  children: PropTypes.node.isRequired,
  config: PropTypes.shape({
    applicationClientId: PropTypes.string,
    loaded: PropTypes.bool,
    authApiBaseUrl: PropTypes.string,
    echannelApiDomainNameHeader: PropTypes.string
  }).isRequired
};
Auth.defaultProps = {};

export { AuthContext };
export default Auth;
